import Vue from 'vue'
import { getSessions, getChatLogs } from '@/api/chat'
import { HOST } from '@/config'
import {
  buildChatMessage,
  buildLoginMessage,
  buildPrivateLoginMessage,
  buildReadMessage,
  shouldSendSessionLogin,
  shouldSendSessionRead,
  isPrivateSessionType,
  clearChatSessionMarks
} from '@/utils/wsProtocol'

const AVATAR_COLORS = {
  group: ['#14b8a6', '#f59e0b', '#8b5cf6'],
  private: ['#60a5fa', '#fb7185'],
  notice: ['#94a3b8']
}

function getSessionUiType(item = {}) {
  const type = String(item.type || '')
  if (type === '1') return 'private'
  if (type === '3') return 'notice'
  if (type === '2' || Number(item.member_count) > 2) return 'group'
  return 'private'
}

function parseLastMessageValue(lastMessage) {
  if (lastMessage == null || lastMessage === '') return null
  if (typeof lastMessage === 'object') return lastMessage
  if (typeof lastMessage !== 'string') return lastMessage
  const trimmed = lastMessage.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return trimmed
  try {
    return JSON.parse(trimmed)
  } catch (e) {
    return trimmed
  }
}

function isAddressMessage(data) {
  if (!data || typeof data !== 'object') return false
  if (data.order_sn || data.tax_service_id || data.service_name || data.price) return false
  return !!(
    data.address ||
    data.full_address ||
    data.address_detail ||
    (data.province_name && data.city_name && data.area_name)
  )
}

function isOrderLikeMessage(data) {
  if (!data || typeof data !== 'object') return false
  return !!(
    data.order_id ||
    data.order_sn ||
    data.order_no ||
    data.tax_service_id ||
    data.service_name ||
    data.price != null
  )
}

function formatLastMessagePreview(lastMessage, messageType = '') {
  const type = String(messageType || '').toLowerCase()
  if (type === 'address') return '【地址】'
  if (type === 'order') return '【订单】'
  if (type === 'image') return '【图片】'

  const parsed = parseLastMessageValue(lastMessage)
  if (parsed && typeof parsed === 'object') {
    if (isOrderLikeMessage(parsed) || type === 'order') {
      return parsed.service_name ? `【订单】${parsed.service_name}` : '【订单】'
    }
    if (isAddressMessage(parsed)) return '【地址】'
    return '【消息】'
  }
  return parsed != null ? String(parsed) : ''
}

function formatSessionTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(Number(timestamp) * 1000)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const pad = n => String(n).padStart(2, '0')
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  if (isSameDay(date, now)) return `${hours}:${minutes}`

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameDay(date, yesterday)) return '昨天'

  const weekStart = new Date(now)
  const day = weekStart.getDay() || 7
  weekStart.setHours(0, 0, 0, 0)
  weekStart.setDate(weekStart.getDate() - day + 1)
  if (date >= weekStart) {
    const weekMap = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
    return weekMap[date.getDay()]
  }

  if (date.getFullYear() === now.getFullYear()) {
    return `${date.getMonth() + 1}月${date.getDate()}日`
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function getStatusMeta(uiType) {
  if (uiType === 'group') {
    return { status: 'processing', statusText: '办理中' }
  }
  if (uiType === 'notice') {
    return { status: 'notice', statusText: '通知' }
  }
  return { status: 'consulting', statusText: '咨询中' }
}

function getAvatarColor(uiType, sessionId = 0) {
  const colors = AVATAR_COLORS[uiType] || AVATAR_COLORS.private
  return colors[Number(sessionId) % colors.length]
}

function resolveAvatarUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url) || String(url).startsWith('data:')) return url
  if (String(url).startsWith('//')) return `https:${url}`
  return `${HOST}${String(url).startsWith('/') ? '' : '/'}${url}`
}

function buildServiceFromSession(raw = {}) {
  const last = parseLastMessageValue(raw.last_message)
  const order = last && typeof last === 'object' ? last : {}

  const title =
    order.service_name || order.name || order.title || ''
  const orderSn = order.order_sn || order.end_order_sn || ''
  const serviceNo =
    orderSn ||
    (order.tax_service_id ? `SV${order.tax_service_id}` : '') ||
    ''
  const region =
    order.area_name ||
    order.region ||
    [order.province_name, order.city_name, order.area_name].filter(Boolean).join(' ') ||
    ''

  const agent =
    raw.kf_name ||
    raw.cs_name ||
    raw.customer_service_name ||
    order.kf_name ||
    order.cs_name ||
    ''
  const fieldWorker =
    raw.wq_name ||
    raw.field_name ||
    raw.worker_name ||
    order.wq_name ||
    order.field_name ||
    ''
  const stage =
    raw.stage_name ||
    raw.current_stage ||
    order.stage_name ||
    order.status_text ||
    (title ? '服务办理中' : '')

  const materialDone = Number(
    raw.material_done ?? raw.uploaded_count ?? order.material_done ?? NaN
  )
  const materialTotal = Number(
    raw.material_total ?? raw.material_count ?? order.material_total ?? NaN
  )

  // 无关联服务信息时也展示右侧面板基础结构
  const hasServiceHint = !!(title || serviceNo || region || raw.tax_chat_pool_id)
  if (!hasServiceHint) return null

  return {
    title: title || raw.display_name || '当前服务',
    id: serviceNo || (raw.tax_chat_pool_id ? `POOL${raw.tax_chat_pool_id}` : ''),
    orderSn,
    taxServiceId: order.tax_service_id || '',
    orderId: order.id || '',
    price: order.price || '',
    stage: stage || '服务办理中',
    region: region || '—',
    agent: agent || '待分配',
    fieldWorker: fieldWorker || '待分配',
    materialDone: Number.isFinite(materialDone) ? materialDone : null,
    materialTotal: Number.isFinite(materialTotal) ? materialTotal : null,
    hasMaterial:
      Number.isFinite(materialDone) &&
      Number.isFinite(materialTotal) &&
      materialTotal > 0
  }
}

function buildMembersFromSession(raw = {}, service = null) {
  if (Array.isArray(raw.members) && raw.members.length) {
    return raw.members.map((m, idx) => {
      const name = m.nickname || m.name || m.user_name || `成员${idx + 1}`
      return {
        id: m.user_id || m.id || idx,
        name,
        role: m.role_name || m.role || m.title || '成员',
        online: m.online === true || m.is_online === 1 || m.is_online === true,
        color: getAvatarColor('private', m.user_id || idx),
        avatar: resolveAvatarUrl(m.full_avatar || m.avatar || '')
      }
    })
  }

  const members = []
  if (service && service.agent && service.agent !== '待分配') {
    members.push({
      id: 'agent',
      name: service.agent,
      role: '专属客服',
      online: true,
      color: '#14b8a6',
      avatar: ''
    })
  }
  if (service && service.fieldWorker && service.fieldWorker !== '待分配') {
    members.push({
      id: 'worker',
      name: service.fieldWorker,
      role: '认证外勤',
      online: true,
      color: '#3b82f6',
      avatar: ''
    })
  }
  members.push({
    id: 'peer',
    name: raw.display_name || '对方',
    role: Number(raw.type) === 2 || Number(raw.member_count) > 2 ? '群成员' : '联系人',
    online: false,
    color: getAvatarColor('private', raw.session_id || 0),
    avatar: resolveAvatarUrl(raw.avatar || '')
  })
  members.push({
    id: 'self',
    name: '您',
    role: '客户',
    online: true,
    color: '#f59e0b',
    avatar: ''
  })
  return members
}

function extractPeerUserId(raw = {}, myId = 0) {
  const candidates = [
    raw.to_user_id,
    raw.peer_id,
    raw.target_user_id,
    raw.other_user_id,
    raw.kf_user_id,
    raw.admin_user_id,
    raw.cs_user_id
  ]
  for (let i = 0; i < candidates.length; i++) {
    const n = Number(candidates[i]) || 0
    if (n && n !== Number(myId)) return n
  }
  const members = raw.members || raw.users || raw.member_list || []
  if (Array.isArray(members)) {
    for (let i = 0; i < members.length; i++) {
      const m = members[i] || {}
      const n = Number(m.user_id != null ? m.user_id : m.id) || 0
      if (n && n !== Number(myId)) return n
    }
  }
  const last = parseLastMessageValue(raw.last_message)
  if (last && typeof last === 'object') {
    const fromId = Number(last.from_user_id || last.sender_id || last.user_id) || 0
    const toId = Number(last.to_user_id) || 0
    if (fromId && fromId !== Number(myId)) return fromId
    if (toId && toId !== Number(myId)) return toId
  }
  const userId = Number(raw.user_id) || 0
  if (userId && userId !== Number(myId)) return userId
  return 0
}

function mapSessionItem(raw = {}, myId = 0) {
  const uiType = getSessionUiType(raw)
  const sessionId = raw.session_id
  const service = buildServiceFromSession(raw)
  let statusMeta = getStatusMeta(uiType)
  if (service && (service.orderSn || service.taxServiceId)) {
    statusMeta = { status: 'processing', statusText: '办理中' }
  }
  const title = raw.display_name || '未命名会话'
  const avatarText = uiType === 'group' ? '群' : title.slice(0, 1)
  const members = buildMembersFromSession(raw, service)
  const memberCount = Number(raw.member_count) || members.length || 0
  const toUserId = extractPeerUserId(raw, myId)

  return {
    id: sessionId,
    sessionId,
    sessionType: String(raw.type || (uiType === 'private' ? '1' : '')),
    taxChatPoolId: raw.tax_chat_pool_id,
    toUserId,
    type: uiType,
    name: title,
    avatar: resolveAvatarUrl(raw.avatar || ''),
    avatarText,
    avatarColor: getAvatarColor(uiType, sessionId),
    status: statusMeta.status,
    statusText: statusMeta.statusText,
    lastMessage: formatLastMessagePreview(
      raw.last_message,
      raw.last_message_type || raw.message_type
    ),
    unread: Number(raw.unread) || 0,
    updatedAt: formatSessionTime(raw.last_time),
    memberCount,
    membersText: memberCount ? `共${memberCount}人` : '',
    service,
    members,
    raw
  }
}

function parseSessionsResponse(res, perPage, currentPage) {
  if (!res || res.code !== 1) {
    return { list: [], lastPage: 1, total: 0 }
  }
  const data = res.data
  if (Array.isArray(data)) {
    return {
      list: data,
      lastPage: data.length < perPage ? currentPage : currentPage + 1,
      total: data.length
    }
  }
  const list = data.data || data.list || []
  return {
    list,
    lastPage: Number(data.last_page) || 1,
    total: Number(data.total) || list.length
  }
}

function parseDateTime(value) {
  if (!value) return Math.floor(Date.now() / 1000)
  if (typeof value === 'number') return value
  const str = String(value)
  if (/^\d+$/.test(str)) return Number(str)
  const ts = Date.parse(str.replace(/-/g, '/'))
  return Number.isNaN(ts) ? Math.floor(Date.now() / 1000) : Math.floor(ts / 1000)
}

function formatChatTime(timestamp) {
  if (!timestamp) return ''
  const seconds = parseDateTime(timestamp)
  const date = new Date(seconds * 1000)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const pad = n => String(n).padStart(2, '0')
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  if (isSameDay(date, now)) return `今天 ${hours}:${minutes}`

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameDay(date, yesterday)) return `昨天 ${hours}:${minutes}`

  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${hours}:${minutes}`
}

function parseMessageObject(message) {
  if (message == null || message === '') return null
  if (typeof message === 'object') return message
  if (typeof message !== 'string') return null
  const trimmed = message.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null
  try {
    return JSON.parse(trimmed)
  } catch (e) {
    return null
  }
}

function resolveMediaUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url) || String(url).startsWith('data:')) return url
  if (String(url).startsWith('//')) return `https:${url}`
  return `${HOST}${String(url).startsWith('/') ? '' : '/'}${url}`
}

function formatQuotePrice(val) {
  const num = Number(val)
  if (Number.isNaN(num)) return String(val || '0')
  return num.toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}

function buildOrderCard(message) {
  let data = message
  if (message != null && typeof message !== 'object' && !String(message).trim().startsWith('{')) {
    data = { id: message, order_id: message }
  } else {
    data = parseMessageObject(message) || (typeof message === 'object' ? message : {}) || {}
  }

  // price=首付款，end_price=尾款
  const downRaw = data.price ?? data.deposit_amount ?? data.stages_price ?? data.deposit ?? ''
  const endRaw = data.end_price ?? data.tail_price ?? data.final_price ?? ''
  const downNum = Number(downRaw)
  const endNum = Number(endRaw)
  const hasDown = downRaw !== '' && downRaw != null && Number.isFinite(downNum)
  const hasEnd = endRaw !== '' && endRaw != null && Number.isFinite(endNum)

  let totalNum = NaN
  if (hasDown && hasEnd) totalNum = downNum + endNum
  else if (hasDown) totalNum = downNum
  else if (hasEnd) totalNum = endNum
  else {
    const fallback = data.total_fee ?? data.amount ?? data.total_price ?? ''
    totalNum = Number(fallback)
  }

  // 定金比例：有首付+尾款时按首付/总价；否则用下行比例字段
  let depositRateText = ''
  const rateFromData = data.deposit_rate ?? data.stages_rate ?? data.deposit_ratio ?? ''
  if (rateFromData !== '' && rateFromData != null) {
    depositRateText = `${String(rateFromData).replace(/%$/, '')}%`
  } else if (hasDown && Number.isFinite(totalNum) && totalNum > 0) {
    depositRateText = `${Math.round((downNum / totalNum) * 10000) / 100}%`
  }

  const orderNo =
    data.order_sn || data.sn || data.end_order_sn || (data.id != null ? `BJ${data.id}` : '')
  const title = data.service_name || data.name || data.title || '服务报价'
  const region =
    data.area_name ||
    data.region ||
    [data.province_name, data.city_name, data.area_name].filter(Boolean).join(' ') ||
    ''

  const statusText =
    data.status_text ||
    data.status_name ||
    (hasDown ? '待付定金' : '待确认')

  const metaParts = []
  if (orderNo) metaParts.push(`订单号 ${orderNo}`)
  if (region) metaParts.push(region)

  return {
    id: data.id || data.order_id || data.tax_service_id || '',
    title,
    orderNo,
    region: region || '',
    metaLine: metaParts.join(' · '),
    /** 服务总价 = 首付 + 尾款 */
    price: Number.isFinite(totalNum) ? formatQuotePrice(totalNum) : formatQuotePrice(0),
    /** 首付款（price） */
    downPayment: hasDown ? formatQuotePrice(downNum) : '',
    /** 尾款（end_price） */
    endPrice: hasEnd ? formatQuotePrice(endNum) : '',
    depositRate: depositRateText,
    /** 本次支付定金 = 首付款 */
    depositAmount: hasDown ? formatQuotePrice(downNum) : '',
    hasDeposit: hasDown,
    statusText,
    desc:
      data.desc ||
      data.description ||
      data.remark ||
      data.service_desc ||
      '',
    cycle: data.cycle || data.expect_cycle || data.period || data.work_days || '',
    raw: data
  }
}

function buildAddressCard(message) {
  let data = message
  if (message != null && typeof message !== 'object' && !String(message).trim().startsWith('{')) {
    data = { id: message }
  } else {
    data = parseMessageObject(message) || (typeof message === 'object' ? message : {}) || {}
  }

  const region = [data.province_name, data.city_name, data.area_name]
    .filter(Boolean)
    .join('')
  const full =
    data.address ||
    data.full_address ||
    [region, data.detail || data.address_detail].filter(Boolean).join(' ')

  return {
    title: data.title || data.name || '地址信息',
    region: region || data.region || '',
    mobile: data.mobile || data.phone || '',
    desc: full || data.desc || '',
    raw: data
  }
}

function mapRemoteMessage(raw = {}, currentUserId, profileHint = null) {
  const sender = raw.sender || {}
  // 兼容推送：content / msg_type / creatime / message_id
  let message = raw.message != null ? raw.message : raw.content
  const messageType = raw.message_type || raw.msg_type || 'text'
  if (typeof message === 'string' && ['order', 'address'].includes(messageType)) {
    message = parseMessageObject(message) || message
  }

  const fromUserId = raw.sender_id ?? sender.id ?? raw.from_user_id
  const isSelf =
    currentUserId &&
    fromUserId != null &&
    String(fromUserId) === String(currentUserId)

  const hint = profileHint || {}
  const nickname =
    sender.nickname ||
    raw.from_user_nickname ||
    raw.nickname ||
    hint.nickname ||
    (isSelf ? '您' : '对方')
  const avatarUrl = resolveMediaUrl(
    sender.full_avatar ||
      sender.avatar ||
      raw.from_user_avatar ||
      raw.avatar ||
      hint.avatar ||
      ''
  )
  const createtime = parseDateTime(raw.createtime || raw.creatime || raw.create_time)
  const fallbackColor = isSelf
    ? '#f59e0b'
    : getAvatarColor('group', fromUserId != null ? fromUserId : 0)

  const base = {
    id: raw.id != null ? raw.id : raw.message_id,
    senderId: fromUserId != null ? String(fromUserId) : '',
    createtime,
    from: isSelf ? 'self' : 'other',
    name: nickname,
    avatarText: String(nickname).slice(0, 1),
    avatarColor: hint.avatarColor || fallbackColor,
    avatarUrl,
    raw
  }

  if (messageType === 'order') {
    const order = buildOrderCard(message)
    const msgStatus =
      raw.status != null
        ? raw.status
        : message && typeof message === 'object'
          ? message.status
          : undefined
    // 聊天记录 status=2：已支付尾款 / 支付完成
    if (Number(msgStatus) === 2) {
      order.finalPaid = true
      order.statusText = '支付完成'
    }
    return {
      ...base,
      type: 'order',
      msgStatus,
      order
    }
  }

  if (messageType === 'address') {
    return {
      ...base,
      type: 'address',
      address: buildAddressCard(message)
    }
  }

  if (messageType === 'image') {
    const src =
      typeof message === 'string'
        ? message
        : (message && (message.url || message.src || message.image)) || ''
    return {
      ...base,
      type: 'image',
      imageUrl: resolveMediaUrl(src)
    }
  }

  if (messageType === 'system' || messageType === 'notice') {
    return {
      id: raw.id,
      type: 'system',
      content: typeof message === 'string' ? message : '系统消息',
      createtime
    }
  }

  return {
    ...base,
    type: 'text',
    content: typeof message === 'string' ? message : formatLastMessagePreview(message, messageType)
  }
}

function parseChatLogsResponse(res) {
  if (!res || res.code !== 1) return []
  const data = res.data
  if (Array.isArray(data)) return data
  return data.data || data.list || []
}

function isPrivateChatSession(session) {
  if (!session) return false
  return session.type === 'private' || String(session.sessionType) === '1'
}

/** 从会话列表原始字段提取头像缓存（session_id / avatar / display_name） */
function buildSessionAvatarCacheItem(rawOrSession = {}) {
  // 兼容已 map 的 session 与 sessions 接口原始项
  const sessionId =
    rawOrSession.session_id != null
      ? rawOrSession.session_id
      : rawOrSession.sessionId != null
        ? rawOrSession.sessionId
        : rawOrSession.id
  if (sessionId == null || sessionId === '') return null

  const rawAvatar =
    rawOrSession.avatar ||
    (rawOrSession.raw && rawOrSession.raw.avatar) ||
    ''
  const avatar = resolveAvatarUrl(rawAvatar)
  if (!avatar) return null

  const nickname =
    rawOrSession.display_name ||
    rawOrSession.name ||
    (rawOrSession.raw && rawOrSession.raw.display_name) ||
    ''

  return {
    sessionId: String(sessionId),
    avatar,
    nickname,
    avatarColor: rawOrSession.avatarColor || '#14b8a6'
  }
}

/** 将会话列表头像写入消息（缺图时强制补上） */
function applySessionAvatarToMessages(list, avatarInfo) {
  if (!Array.isArray(list) || !avatarInfo || !avatarInfo.avatar) return list
  list.forEach(m => {
    if (!m || m.type === 'time' || m.type === 'system') return
    // 自己的消息不套对方会话头像
    if (m.from === 'self') return
    const needAvatar = !m.avatarUrl
    const needName = !m.name || m.name === '对方'
    if (!needAvatar && !needName) return
    if (needAvatar) Vue.set(m, 'avatarUrl', avatarInfo.avatar)
    if (needName && avatarInfo.nickname) {
      Vue.set(m, 'name', avatarInfo.nickname)
      Vue.set(m, 'avatarText', String(avatarInfo.nickname).slice(0, 1))
    }
    if (avatarInfo.avatarColor) {
      Vue.set(m, 'avatarColor', avatarInfo.avatarColor)
    }
  })
  return list
}

function resolveSenderProfile(state, session, senderId, currentUserId) {
  if (senderId == null || senderId === '') return null
  const key = String(senderId)
  const cached = state.senderProfiles[key]

  // 私聊对方：会话列表头像优先作为兜底（不依赖 toUserId 是否已解析）
  let sessionPeerAvatar = ''
  let sessionPeerName = ''
  let sessionPeerColor = ''
  if (
    isPrivateChatSession(session) &&
    (!currentUserId || String(currentUserId) !== key)
  ) {
    sessionPeerAvatar = session.avatar || ''
    if (session.name && session.name !== '未命名会话') {
      sessionPeerName = session.name
    }
    sessionPeerColor = session.avatarColor || ''
  }

  // 群成员头像
  let memberAvatar = ''
  let memberName = ''
  let memberColor = ''
  if (session && Array.isArray(session.members)) {
    const member = session.members.find(m => String(m.id) === key)
    if (member) {
      memberAvatar = member.avatar || ''
      memberName = member.name || ''
      memberColor = member.color || ''
    }
  }

  const fallbackAvatar = sessionPeerAvatar || memberAvatar || ''
  const fallbackName = sessionPeerName || memberName || ''
  const fallbackColor = sessionPeerColor || memberColor || ''

  if (cached && cached.nickname && cached.nickname !== '对方') {
    return {
      nickname: cached.nickname,
      avatar: cached.avatar || fallbackAvatar || '',
      avatarColor: cached.avatarColor || fallbackColor || '#14b8a6'
    }
  }

  // 从当前会话历史消息里找同 sender
  if (session && state.messages[session.id]) {
    const found = state.messages[session.id].find(
      m =>
        m &&
        m.from === 'other' &&
        m.name &&
        m.name !== '对方' &&
        (String(m.senderId) === key ||
          (m.raw &&
            String(m.raw.sender_id || (m.raw.sender && m.raw.sender.id) || '') === key))
    )
    if (found) {
      return {
        nickname: found.name,
        avatar: found.avatarUrl || fallbackAvatar || '',
        avatarColor: found.avatarColor || fallbackColor || '#14b8a6'
      }
    }
  }

  if (fallbackName) {
    return {
      nickname: fallbackName,
      avatar: fallbackAvatar || '',
      avatarColor: fallbackColor || '#14b8a6'
    }
  }

  if (cached) {
    return {
      nickname: cached.nickname || '对方',
      avatar: cached.avatar || fallbackAvatar || '',
      avatarColor: cached.avatarColor || fallbackColor || '#14b8a6'
    }
  }
  if (currentUserId && String(currentUserId) === key) {
    return { nickname: '您', avatar: '', avatarColor: '#f59e0b' }
  }
  if (fallbackAvatar) {
    return {
      nickname: '对方',
      avatar: fallbackAvatar,
      avatarColor: fallbackColor || '#14b8a6'
    }
  }
  return null
}

function cacheProfileFromMapped(commit, mapped) {
  if (!mapped || mapped.from !== 'other' || !mapped.senderId) return
  if ((!mapped.name || mapped.name === '对方') && !mapped.avatarUrl) return
  commit('CACHE_SENDER_PROFILE', {
    userId: mapped.senderId,
    nickname: mapped.name,
    avatar: mapped.avatarUrl || '',
    avatarColor: mapped.avatarColor
  })
}

function withTimeDividers(messages) {
  const result = []
  let lastTs = 0
  messages.forEach(msg => {
    const ts = Number(msg.createtime) || 0
    if (!lastTs || ts - lastTs >= 10 * 60) {
      result.push({
        id: `time_${msg.id || ts}`,
        type: 'time',
        content: formatChatTime(ts)
      })
    }
    result.push(msg)
    lastTs = ts || lastTs
  })
  return result
}

const state = {
  sessions: [],
  currentSessionId: null,
  messages: {},
  /** 发送者资料缓存：userId -> { nickname, avatar, avatarColor } */
  senderProfiles: {},
  /** 会话列表头像缓存：sessionId -> { avatar, nickname, avatarColor } */
  sessionAvatarCache: {},
  page: 1,
  perPage: 10,
  lastPage: 1,
  total: 0,
  loading: false,
  loadStatus: 'loadmore',
  messagesLoading: false,
  /** 已发出会话 login，等待 login_success */
  sessionLoginPending: false
}

const mutations = {
  SET_SESSIONS(state, { list, append }) {
    state.sessions = append ? [...state.sessions, ...list] : list
  },
  SET_PAGE_META(state, { page, lastPage, total, loadStatus }) {
    if (page != null) state.page = page
    if (lastPage != null) state.lastPage = lastPage
    if (total != null) state.total = total
    if (loadStatus != null) state.loadStatus = loadStatus
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_MESSAGES_LOADING(state, loading) {
    state.messagesLoading = loading
  },
  SET_SESSION_LOGIN_PENDING(state, pending) {
    state.sessionLoginPending = !!pending
  },
  SET_CURRENT_SESSION(state, sessionId) {
    state.currentSessionId = sessionId
    const session = state.sessions.find(item => item.id === sessionId)
    if (session) {
      session.unread = 0
    }
  },
  SET_MESSAGES(state, { sessionId, list }) {
    const key = String(sessionId)
    let cached = state.sessionAvatarCache[key]
    // 缓存未命中时，直接从 sessions 列表按 session_id 取 avatar / display_name
    if (!cached || !cached.avatar) {
      const session = state.sessions.find(
        item => String(item.id) === key || String(item.sessionId) === key
      )
      const built = buildSessionAvatarCacheItem(session || {})
      if (built) {
        Vue.set(state.sessionAvatarCache, built.sessionId, {
          avatar: built.avatar,
          nickname: built.nickname,
          avatarColor: built.avatarColor
        })
        cached = state.sessionAvatarCache[built.sessionId]
      }
    }
    const nextList = applySessionAvatarToMessages(list || [], cached)
    Vue.set(state.messages, sessionId, nextList)
  },
  /** 缓存会话列表头像：key = session_id，值 = avatar + display_name */
  CACHE_SESSION_AVATAR(state, sessionOrRaw) {
    const built = buildSessionAvatarCacheItem(sessionOrRaw || {})
    if (!built) return
    Vue.set(state.sessionAvatarCache, built.sessionId, {
      avatar: built.avatar,
      nickname: built.nickname,
      avatarColor: built.avatarColor
    })
  },
  /** 咨询创建后：若会话列表还没有，插入一条占位会话 */
  UPSERT_SESSION_FROM_ZIXUN(state, payload = {}) {
    const sessionId = payload.session_id
    const poolId = payload.tax_chat_pool_id || payload.pool_id
    if (sessionId == null && poolId == null) return
    const key = sessionId != null ? sessionId : poolId
    const exists = state.sessions.find(
      s =>
        (sessionId != null && String(s.sessionId) === String(sessionId)) ||
        (poolId != null && String(s.taxChatPoolId) === String(poolId)) ||
        String(s.id) === String(key)
    )
    if (exists) {
      if (payload.to_user_id && !exists.toUserId) {
        exists.toUserId = Number(payload.to_user_id) || exists.toUserId
      }
      return
    }
    const name = payload.display_name || '客服咨询'
    const sessionType = String(payload.session_type || payload.type || '1')
    const uiType = sessionType === '1' ? 'private' : getSessionUiType({ type: sessionType })
    state.sessions.unshift({
      id: key,
      sessionId: sessionId != null ? sessionId : key,
      sessionType,
      taxChatPoolId: poolId,
      toUserId: Number(payload.to_user_id) || 0,
      type: uiType,
      name,
      avatar: resolveAvatarUrl(payload.avatar || ''),
      avatarText: name.slice(0, 1),
      avatarColor: getAvatarColor(uiType, key),
      status: 'consulting',
      statusText: '咨询中',
      lastMessage: '',
      unread: 0,
      updatedAt: '',
      memberCount: 2,
      membersText: '共2人',
      service: null,
      members: [],
      raw: payload
    })
  },
  APPLY_SESSION_AVATAR_TO_MESSAGES(state, sessionId) {
    const key = String(sessionId)
    let cached = state.sessionAvatarCache[key]
    if (!cached || !cached.avatar) {
      const session = state.sessions.find(
        item => String(item.id) === key || String(item.sessionId) === key
      )
      const built = buildSessionAvatarCacheItem(session || {})
      if (built) {
        Vue.set(state.sessionAvatarCache, built.sessionId, {
          avatar: built.avatar,
          nickname: built.nickname,
          avatarColor: built.avatarColor
        })
        cached = state.sessionAvatarCache[built.sessionId]
      }
    }
    if (!cached || !cached.avatar) return
    const list = state.messages[sessionId] || state.messages[key]
    if (!list || !list.length) return
    applySessionAvatarToMessages(list, cached)
  },
  CACHE_SENDER_PROFILE(state, { userId, nickname, avatar, avatarColor }) {
    if (userId == null || userId === '') return
    const key = String(userId)
    const prev = state.senderProfiles[key] || {}
    // 不覆盖已有真实昵称（避免被「对方」写回）
    const nextName =
      nickname && nickname !== '对方' && nickname !== '您'
        ? nickname
        : prev.nickname || nickname
    const nextAvatar = avatar || prev.avatar || ''
    const nextColor = avatarColor || prev.avatarColor || '#14b8a6'
    const profile = {
      nickname: nextName || prev.nickname || '对方',
      avatar: nextAvatar,
      avatarColor: nextColor
    }
    Vue.set(state.senderProfiles, key, profile)

    // 回填已渲染消息：补昵称 / 补头像图
    const hasRealName = profile.nickname && profile.nickname !== '对方'
    if (!hasRealName && !profile.avatar) return

    Object.keys(state.messages).forEach(sid => {
      const list = state.messages[sid] || []
      list.forEach(m => {
        if (!m || m.from !== 'other' || String(m.senderId) !== key) return
        if (hasRealName && (!m.name || m.name === '对方')) {
          m.name = profile.nickname
          m.avatarText = String(profile.nickname).slice(0, 1)
        }
        if (profile.avatarColor) m.avatarColor = profile.avatarColor
        if (profile.avatar && !m.avatarUrl) m.avatarUrl = profile.avatar
      })
    })
  },
  ADD_MESSAGE(state, { sessionId, message, bumpUnread = false }) {
    if (!state.messages[sessionId]) {
      Vue.set(state.messages, sessionId, [])
    }
    // 按 id 去重
    if (message.id != null) {
      const exists = state.messages[sessionId].some(
        m => m.id != null && String(m.id) === String(message.id)
      )
      if (exists) return
    }
    // 实时消息缺头像时，用会话列表缓存补上
    const cached = state.sessionAvatarCache[String(sessionId)]
    if (
      message &&
      message.from === 'other' &&
      !message.avatarUrl &&
      cached &&
      cached.avatar
    ) {
      Vue.set(message, 'avatarUrl', cached.avatar)
      if ((!message.name || message.name === '对方') && cached.nickname) {
        Vue.set(message, 'name', cached.nickname)
        Vue.set(message, 'avatarText', String(cached.nickname).slice(0, 1))
      }
    }
    state.messages[sessionId].push(message)

    const session = state.sessions.find(item => item.id === sessionId)
    if (session) {
      const preview =
        message.type === 'order'
          ? '【订单】'
          : message.type === 'address'
            ? '【地址】'
            : message.type === 'image'
              ? '【图片】'
              : message.content || session.lastMessage
      session.lastMessage = preview
      session.updatedAt = message.time || session.updatedAt
      if (bumpUnread && message.from === 'other' && state.currentSessionId !== sessionId) {
        session.unread = (session.unread || 0) + 1
      }
    }
  }
}

const actions = {
  async fetchSessions({ commit, state, rootState }, { refresh = false } = {}) {
    if (state.loadStatus === 'loading') return
    if (!refresh && state.loadStatus === 'nomore') return

    const page = refresh ? 1 : state.page
    if (refresh) {
      commit('SET_PAGE_META', {
        page: 1,
        lastPage: 1,
        total: 0,
        loadStatus: 'loading'
      })
    } else {
      commit('SET_PAGE_META', { loadStatus: 'loading' })
    }

    commit('SET_LOADING', refresh && !state.sessions.length)

    try {
      const res = await getSessions({
        page,
        per_page: state.perPage
      })
      const { list, lastPage, total } = parseSessionsResponse(
        res,
        state.perPage,
        page
      )
      const userInfo = (rootState.user && rootState.user.userInfo) || {}
      const myId = userInfo.id || userInfo.user_id || 0
      const mapped = list.map(item => mapSessionItem(item, myId))
      commit('SET_SESSIONS', { list: mapped, append: !refresh })
      // 按接口字段 session_id + avatar + display_name 缓存
      list.forEach(raw => commit('CACHE_SESSION_AVATAR', raw))
      mapped.forEach(session => {
        commit('CACHE_SESSION_AVATAR', session)
        if (session.toUserId && session.avatar) {
          commit('CACHE_SENDER_PROFILE', {
            userId: session.toUserId,
            nickname: session.name,
            avatar: session.avatar,
            avatarColor: session.avatarColor
          })
        }
      })

      const reachedEnd =
        !mapped.length ||
        mapped.length < state.perPage ||
        page >= lastPage

      commit('SET_PAGE_META', {
        page: reachedEnd ? page : page + 1,
        lastPage,
        total,
        loadStatus: reachedEnd ? 'nomore' : 'loadmore'
      })
    } catch (e) {
      commit('SET_PAGE_META', { loadStatus: 'loadmore' })
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async selectSession({ commit, dispatch, state }, sessionId) {
    commit('SET_CURRENT_SESSION', sessionId)
    const session = state.sessions.find(item => item.id === sessionId)
    if (session) {
      commit('CACHE_SESSION_AVATAR', session)
    }
    await dispatch('fetchChatLogs', sessionId)
    await dispatch('joinSession', sessionId)
  },

  async fetchChatLogs({ commit, state, rootState }, sessionId) {
    const id = sessionId || state.currentSessionId
    const session = state.sessions.find(item => item.id === id)
    if (!session) return

    const poolId = session.taxChatPoolId
    if (!poolId) {
      commit('SET_MESSAGES', { sessionId: id, list: [] })
      return
    }

    commit('SET_MESSAGES_LOADING', true)
    try {
      // 打开对话：确保按 session_id 缓存了列表里的 avatar / display_name
      commit('CACHE_SESSION_AVATAR', session)
      if (session.raw) commit('CACHE_SESSION_AVATAR', session.raw)

      const params = { tax_chat_pool_id: poolId }
      if (session.sessionId) {
        params.session_id = session.sessionId
      }
      const res = await getChatLogs(params)
      const logs = parseChatLogsResponse(res)
      const userInfo = (rootState.user && rootState.user.userInfo) || {}
      const currentUserId = userInfo.id || userInfo.user_id
      // 从历史补充 toUserId
      if (!session.toUserId && logs.length) {
        const peer = extractPeerUserId(
          { last_message: logs[logs.length - 1], members: session.raw && session.raw.members },
          currentUserId
        )
        if (peer) session.toUserId = peer
      }

      const cacheKey = String(id)
      const sessionAvatar =
        state.sessionAvatarCache[cacheKey] ||
        buildSessionAvatarCacheItem(session) ||
        buildSessionAvatarCacheItem(session.raw || {})

      const mapped = logs
        .map(item => {
          const sender = item.sender || {}
          const senderId = item.sender_id ?? sender.id ?? item.from_user_id
          const hint = resolveSenderProfile(state, session, senderId, currentUserId)
          const mergedHint = {
            ...(hint || {}),
            avatar:
              (hint && hint.avatar) ||
              (sessionAvatar && sessionAvatar.avatar) ||
              '',
            nickname:
              (hint && hint.nickname && hint.nickname !== '对方'
                ? hint.nickname
                : '') ||
              (sessionAvatar && sessionAvatar.nickname) ||
              '',
            avatarColor:
              (hint && hint.avatarColor) ||
              (sessionAvatar && sessionAvatar.avatarColor) ||
              ''
          }
          const msg = mapRemoteMessage(item, currentUserId, mergedHint)
          cacheProfileFromMapped(commit, msg)
          return msg
        })
        .sort((a, b) => {
          const ta = Number(a.createtime) || 0
          const tb = Number(b.createtime) || 0
          if (ta !== tb) return ta - tb
          return (Number(a.id) || 0) - (Number(b.id) || 0)
        })

      // 入库时按 session_id 缓存头像回填历史消息
      commit('SET_MESSAGES', {
        sessionId: id,
        list: withTimeDividers(mapped)
      })
      commit('APPLY_SESSION_AVATAR_TO_MESSAGES', id)
    } catch (e) {
      commit('SET_MESSAGES', { sessionId: id, list: [] })
    } finally {
      commit('SET_MESSAGES_LOADING', false)
    }
  },

  /** 进入会话：发会话 login，必要时带 read */
  async joinSession({ state, commit, dispatch, rootGetters }, sessionId) {
    const id = sessionId || state.currentSessionId
    const session = state.sessions.find(item => item.id === id)
    if (!session) return false

    await dispatch('websocket/connectAppWebSocket', {}, { root: true })
    dispatch('websocket/markSessionReady', false, { root: true })
    clearChatSessionMarks()

    const isPrivate = isPrivateSessionType(session.sessionType) || session.type === 'private'
    const loginOptions = {
      tax_chat_pool_id: session.taxChatPoolId,
      session_id: session.sessionId,
      to_user_id: session.toUserId,
      is_private: isPrivate
    }

    if (!shouldSendSessionLogin(loginOptions) && rootGetters['websocket/wsSessionReady']) {
      return true
    }

    const payload = isPrivate
      ? buildPrivateLoginMessage({
          to_user_id: session.toUserId,
          tax_chat_pool_id: session.taxChatPoolId,
          session_id: session.sessionId
        })
      : buildLoginMessage({
          tax_chat_pool_id: session.taxChatPoolId,
          session_id: session.sessionId,
          to_user_id: session.toUserId
        })

    await dispatch('websocket/sendMessage', payload, { root: true })
    commit('SET_SESSION_LOGIN_PENDING', true)

    const ready = await new Promise(resolve => {
      const start = Date.now()
      const timer = setInterval(() => {
        if (rootGetters['websocket/wsSessionReady']) {
          clearInterval(timer)
          resolve(true)
        } else if (Date.now() - start > 8000) {
          clearInterval(timer)
          resolve(false)
        }
      }, 200)
    })
    commit('SET_SESSION_LOGIN_PENDING', false)

    if (session.sessionId && shouldSendSessionRead(session.sessionId)) {
      dispatch(
        'websocket/sendMessage',
        buildReadMessage({ session_id: session.sessionId }),
        { root: true }
      )
    }
    return ready
  },

  onWsLoginSuccess({ state, commit, dispatch }) {
    // 仅在已发出「会话 login」后，才把 login_success 视为会话就绪
    if (state.sessionLoginPending) {
      dispatch('websocket/markSessionReady', true, { root: true })
      commit('SET_SESSION_LOGIN_PENDING', false)
    }
  },

  onWsError({ dispatch }, data) {
    const errMsg = (data && (data.msg || data.message)) || ''
    if (/not in this session/i.test(String(errMsg))) {
      dispatch('websocket/markSessionReady', false, { root: true })
      dispatch('joinSession')
    }
  },

  onWsChatMessage({ commit, state, rootState, dispatch }, raw) {
    const userInfo = (rootState.user && rootState.user.userInfo) || {}
    const currentUserId = userInfo.id || userInfo.user_id

    const poolId = raw.tax_chat_pool_id
    const sid = raw.session_id
    let session =
      state.sessions.find(
        s =>
          (sid && String(s.sessionId) === String(sid)) ||
          (poolId && String(s.taxChatPoolId) === String(poolId))
      ) || null

    const sender = raw.sender || {}
    const senderId = raw.sender_id ?? sender.id ?? raw.from_user_id
    const hint = resolveSenderProfile(state, session, senderId, currentUserId)
    const mapped = mapRemoteMessage(raw, currentUserId, hint)
    if (
      isPrivateChatSession(session) &&
      session.avatar &&
      mapped.from === 'other' &&
      !mapped.avatarUrl
    ) {
      mapped.avatarUrl = session.avatar
    }
    cacheProfileFromMapped(commit, mapped)

    if (!session) {
      dispatch('fetchSessions', { refresh: true }).then(() => {
        const again = state.sessions.find(
          s =>
            (sid && String(s.sessionId) === String(sid)) ||
            (poolId && String(s.taxChatPoolId) === String(poolId))
        )
        if (again) {
          // 会话刷新后再用会话名补一次资料
          const retryHint = resolveSenderProfile(state, again, senderId, currentUserId)
          const retryMapped =
            retryHint && mapped.name === '对方'
              ? mapRemoteMessage(raw, currentUserId, retryHint)
              : mapped
          cacheProfileFromMapped(commit, retryMapped)
          commit('ADD_MESSAGE', {
            sessionId: again.id,
            message: retryMapped,
            bumpUnread: true
          })
        }
      })
      return
    }

    // 补齐 peer
    if (!session.toUserId && mapped.from === 'other' && raw.sender_id) {
      session.toUserId = Number(raw.sender_id) || session.toUserId
    }

    const isCurrent = state.currentSessionId === session.id
    commit('ADD_MESSAGE', {
      sessionId: session.id,
      message: mapped,
      bumpUnread: !isCurrent
    })
  },

  /**
   * 通过 WebSocket 发送聊天消息
   */
  async sendChatMessage({ commit, state, rootState, dispatch, rootGetters }, payload = {}) {
    const sessionId = state.currentSessionId
    const session = state.sessions.find(item => item.id === sessionId)
    if (!session) throw new Error('请先选择会话')

    const message = payload.message
    const messageType = payload.message_type || 'text'
    if (message == null || message === '') throw new Error('消息不能为空')

    await dispatch('websocket/connectAppWebSocket', {}, { root: true })
    if (!rootGetters['websocket/wsSessionReady']) {
      await dispatch('joinSession', sessionId)
    }

    const isPrivate = isPrivateSessionType(session.sessionType) || session.type === 'private'

    if (isPrivate && !session.toUserId) {
      throw new Error('缺少对方用户，无法发送私聊消息')
    }
    if (!session.taxChatPoolId && isPrivate) {
      throw new Error('缺少聊天池，无法发送消息')
    }

    const wsPayload = buildChatMessage({
      session_type: isPrivate ? '1' : session.sessionType,
      session_id: session.sessionId,
      to_user_id: session.toUserId,
      tax_chat_pool_id: session.taxChatPoolId,
      message,
      message_type: messageType
    })

    await dispatch('websocket/sendMessage', wsPayload, { root: true })

    const userInfo = (rootState.user && rootState.user.userInfo) || {}
    const optimistic = mapRemoteMessage(
      {
        id: `local_${Date.now()}`,
        message_type: messageType,
        message,
        sender_id: userInfo.id || userInfo.user_id,
        sender: {
          nickname: userInfo.nickname || userInfo.name || '您',
          avatar: userInfo.avatar || ''
        },
        createtime: Math.floor(Date.now() / 1000)
      },
      userInfo.id || userInfo.user_id
    )
    commit('ADD_MESSAGE', { sessionId, message: optimistic, bumpUnread: false })
    return true
  },

  /** 兼容旧调用：发文本 */
  async sendMessage({ dispatch }, content) {
    if (!content || !String(content).trim()) return
    return dispatch('sendChatMessage', {
      message: String(content).trim(),
      message_type: 'text'
    })
  },

  async sendOrderCard({ dispatch }, order) {
    if (!order) throw new Error('请选择订单')
    const message = typeof order === 'object' ? order : Number(order) || order
    return dispatch('sendChatMessage', {
      message,
      message_type: 'order'
    })
  },

  async sendAddressCard({ dispatch }, address) {
    if (!address) throw new Error('请填写地址')
    const message = typeof address === 'object' ? address : Number(address) || address
    return dispatch('sendChatMessage', {
      message,
      message_type: 'address'
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
